<template>
  <div>
    <div class="tw-flex tw-items-center tw-justify-between">
      <h2 class="tw-text-lg tw-leading-7 tw-font-normal tw-text-gray-800 tw-mb-0">
        Notifications
      </h2>

      <base-button v-if="showMarkAllAsRead" type="primary" class="tw-whitespace-nowrap" :disabled="isMarkingAll" @click="markAsRead(null, true)">
        <span v-if="!isMarkingAll">Mark All as Read</span>
        <span v-else>
          Marking...
        </span>
      </base-button>
    </div>

    <div class="tw-mt-8">
      <div class="tw-overflow-x-auto tw-mt-2">
        <base-table :loading="isLoading" :fields="fields" :items="notifications">
          <!-- message -->
          <template #message="{ item: member }">
            <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-break-words tw-whitespace-pre-line tw-max-w-2xl" v-html="convertNewLinesToBreaks(member.message)"></div>
          </template>

          <!-- insertedAt -->
          <template #insertedAt="{ item: member }">
            <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">
              {{ formatDate(member.insertedAt) }}
            </div>
          </template>

          <!-- insertedByFullName -->
          <template #insertedByFullName="{ item: member }">
            <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">
              {{ member.insertedByFullName }}
            </div>
          </template>

          <!-- totalReads -->
          <template #totalReads="{ item: member }">
            <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">
              {{ member.totalReads }}
            </div>
          </template>

          <!-- actions -->
          <template #actions="{ item: member }">
            <div class="tw-flex tw-items-center tw-space-x-2" v-if="!member.isRead">
              <base-button
                type="flat"
                :disabled="notificationLoading[member.businessNotificationId]"
                class="tw-whitespace-nowrap"
                @click="markAsRead(member.businessNotificationId)"
              >
                <span v-if="!notificationLoading[member.businessNotificationId]">Mark as Read</span>
                <span v-else>
                  <base-spinner class="tw-w-4 tw-h-4 tw-align-middle" />
                </span>
              </base-button>
            </div>
          </template>

          <!-- Empty State -->
          <template #empty-state>
            <div class="tw-flex tw-justify-center tw-py-5">
              No Results
            </div>
          </template>

          <!-- Loading Skeleton -->
          <template #loading-row>
            <td class="tw-py-3 tw-px-4">
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
            </td>
            <td class="tw-py-3 tw-px-4">
              <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
            </td>
            <td class="tw-py-3 tw-px-4">
              <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
            </td>
            <td class="tw-py-3 tw-px-4">
              <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
            </td>
          </template>
        </base-table>
        <base-pagination v-if="page.total > page.take" v-model="page" class="tw-mt-2" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import axios from "axios";

const TAKE_KEY = "predictile_business_notifications_take";

export default {
  name: "SalesNotificationManager",
  props: {
    businessId: String,
    initialItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isFormValid: false,
      isSubmitting: false,
      isLoading: false,
      isMarkingAll: false, // NEW
      notificationLoading: {},
      notification: {
        message: "",
      },
      notifications: this.initialItems,
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
    };
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["locations"]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered"]),
    fields() {
      let columns = [];

      columns.push({ title: "Notification Message", key: "message", thClass: "" });
      columns.push({ title: this.isLocationFiltered === true ? "Received At" : "Sent At", key: "insertedAt" });
      columns.push({ title: "Sent By", key: "insertedByFullName" });
      if (this.isLocationFiltered === false) columns.push({ title: "Read Count", key: "totalReads" });
      if (this.isLocationFiltered === true) columns.push({ title: "Actions", key: "actions" });

      return columns;
    },
    showMarkAllAsRead() {
      return this.isLocationFiltered && this.notifications.some((n) => !n.isRead);
    },
  },
  created() {
    this.EventBus.$on("NOTIFICATION_SENT", this.loadPage);
    if (!this.initialItems.length) {
      this.loadPage(); // fallback if no props passed
    }
  },
  methods: {
    formatDate(date) {
      return moment
        .utc(date)
        .local()
        .format("M/D/YY h:mm A");
    },

    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },

    loadPage() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/notifications?skip=${this.page.skip}&take=${this.page.take}`;
      this.$axios
        .get(url)
        .then((res) => {
          this.notifications = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load notifications",
            message: "We had a problem retrieving the list of notifications. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    truncateToWords(text, wordLimit = 50) {
      if (!text || typeof text !== "string") return "";
      const words = text.trim().split(/\s+/);
      if (words.length <= wordLimit) {
        return words.join(" ");
      }
      return words.slice(0, wordLimit).join(" ") + "...";
    },

    convertNewLinesToBreaks(text) {
      if (!text) return "";
      return text.replace(/\n/g, "<br />");
    },

    async markAsRead(id, markAll = false) {
      if (markAll) {
        this.isMarkingAll = true;
      } else {
        this.$set(this.notificationLoading, id, true);
      }

      try {
        const endpointId = id !== null && id !== undefined ? id : "all";
        await axios.post(`/businesses/${this.businessId}/notification/read/${endpointId}`, {
          markAll,
        });

        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Mark as read",
          message: markAll ? "All notifications have been marked as read." : "The notification has been marked as read successfully.",
        });

        if (markAll) {
          this.notifications.forEach((n) => (n.isRead = true));
        } else {
          const index = this.notifications.findIndex((n) => n.businessNotificationId === id);
          if (index !== -1) {
            this.notifications[index].isRead = true;
          }
        }
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to mark as read",
          message: markAll ? "Could not mark all notifications as read. Please try again." : "We were not able to mark the notification as read. Please try again.",
          error,
        });
      } finally {
        if (markAll) {
          this.isMarkingAll = false;
        } else {
          this.$delete(this.notificationLoading, id);
        }
      }
    },
  },
};
</script>
